// Lightweight API client for SkinMemory AI
import cached from "./cache";
import storage from "./storage";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;

async function req(method, path, body) {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
    credentials: "include",
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(`${method} ${path} → ${res.status} ${text}`);
    // Attach the status so callers can branch on it without regex-matching
    // the message string (e.g. skin-checkin quota handling).
    err.status = res.status;
    err.body = text;
    throw err;
  }
  // Some endpoints may return null (Optional[Product])
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

export const api = {
  // Internal raw helper — exposed for ad-hoc callers (analytics.js).
  req,

  // Users
  upsertUser: (payload) => req("POST", "/users", payload),
  getUser: (userId) => req("GET", `/users/${encodeURIComponent(userId)}`),
  // Cached variant — fed by RouteProRefresher on every nav, so we don't
  // hammer the backend. TTL=60s gives near-instant nav while still
  // catching webhook-driven is_pro flips within a minute.
  getUserCached: (userId) =>
    cached(
      `users:${userId}`,
      () => req("GET", `/users/${encodeURIComponent(userId)}`),
      { ttl: 60_000 },
    ),
  invalidateUser: (userId) => cached.invalidate(`users:${userId}`),
  mergeUser: (fromId, toId) => req("POST", "/merge-user", { from_user_id: fromId, to_user_id: toId }),

  // Email auth
  authRegister: async (email, password, anonymousUserId, extra = {}) => {
    const res = await req("POST", "/auth/register", {
      email, password, anonymous_user_id: anonymousUserId,
      first_name: extra.firstName || null, last_name: extra.lastName || null,
    });
    // Clear any cached entries tied to the old anonymous UUID so the next
    // proContext refresh hits the new authed user row directly.
    try {
      if (anonymousUserId) {
        cached.invalidate(`users:${anonymousUserId}`);
        api.invalidateSuspects(anonymousUserId);
      }
      if (res?.id) {
        cached.invalidate(`users:${res.id}`);
        api.invalidateSuspects(res.id);
        // Persist authed id so all subsequent reads/writes use it.
        storage.setAuthedUserId(res.id);
        // Clear any lingering logout epoch so authMe will happily rehydrate
        // for the newly authed session.
        try { sessionStorage.removeItem("sm_logout_epoch"); } catch { /* */ }
      }
      // Force the identity cache + boot promise to re-resolve so the
      // very next syncUserState broadcasts under the authed uid.
      const { resetIdentity, resetBootSync, bootSync } = await import("./sync");
      resetIdentity?.();
      resetBootSync?.();
      // Fire-and-forget a fresh boot sync under the authed uid so
      // SuspectPanel + counters update immediately after login.
      bootSync?.().catch(() => { /* */ });
    } catch { /* */ }
    return res;
  },
  authLogin: async (email, password, anonymousUserId) => {
    const res = await req("POST", "/auth/login", { email, password, anonymous_user_id: anonymousUserId });
    try {
      if (anonymousUserId) {
        cached.invalidate(`users:${anonymousUserId}`);
        api.invalidateSuspects(anonymousUserId);
      }
      if (res?.id) {
        cached.invalidate(`users:${res.id}`);
        api.invalidateSuspects(res.id);
        storage.setAuthedUserId(res.id);
        try { sessionStorage.removeItem("sm_logout_epoch"); } catch { /* */ }
      }
      const { resetIdentity, resetBootSync, bootSync } = await import("./sync");
      resetIdentity?.();
      resetBootSync?.();
      bootSync?.().catch(() => { /* */ });
    } catch { /* */ }
    return res;
  },
  authLogout: async () => {
    const res = await req("POST", "/auth/logout");
    // Caller will reset localStorage; nuke all caches so the next visit
    // starts fresh. Also reset the cached identity + boot promises so any
    // subsequent refreshPro/bootSync re-resolves anon (not the just-logged-out
    // uid). Set a sessionStorage epoch so any in-flight authMe response
    // that arrives AFTER logout can't rehydrate sm_authed_user_id.
    try {
      cached.clear();
      storage.clearAuthedUserId();
      try { sessionStorage.setItem("sm_logout_epoch", String(Date.now())); } catch { /* */ }
      const { resetIdentity, resetBootSync } = await import("./sync");
      resetIdentity?.();
      resetBootSync?.();
    } catch { /* */ }
    return res;
  },
  forgotPassword: (email) => req("POST", "/auth/forgot-password", { email }),
  resetPassword: (token, newPassword) =>
    req("POST", "/auth/reset-password", { token, new_password: newPassword }),
  authMe: async () => {
    const res = await req("GET", "/auth/me");
    // Cookie-based session may be valid even when localStorage was cleared
    // (e.g. private tab, cross-device). Reconcile the authed id from the
    // canonical server response so all subsequent reads/writes are correct.
    // BUT: if we JUST logged out (within the last 3s), an in-flight authMe
    // response can rehydrate sm_authed_user_id before the browser processes
    // the Set-Cookie clearing header — refuse to persist in that window.
    try {
      if (res?.id) {
        let inLogoutWindow = false;
        try {
          const epoch = Number(sessionStorage.getItem("sm_logout_epoch") || 0);
          inLogoutWindow = epoch > 0 && (Date.now() - epoch) < 3000;
        } catch { /* */ }
        if (!inLogoutWindow) storage.setAuthedUserId(res.id);
      }
    } catch { /* */ }
    return res;
  },
  authRefresh: () => req("POST", "/auth/refresh"),

  // Stats counters
  getScansCount: (userId) => req("GET", `/scans/count/${encodeURIComponent(userId)}`),
  getReactionsCount: (userId) => req("GET", `/reactions/count/${encodeURIComponent(userId)}`),
  listSuspects: (userId, limit = null) =>
    req(
      "GET",
      `/suspect-ingredients/${encodeURIComponent(userId)}${limit ? `?limit=${limit}` : ""}`,
    ),
  // Cached variant — TTL=120s. Invalidated explicitly after a reaction is
  // logged (see createReaction below) so the panel always reflects fresh
  // suspect scores on the next nav after a save.
  listSuspectsCached: (userId, limit = null) =>
    cached(
      `suspects:${userId}${limit ? `:${limit}` : ""}`,
      () => api.listSuspects(userId, limit),
      { ttl: 120_000 },
    ),
  invalidateSuspects: (userId) => {
    cached.invalidate(`suspects:${userId}`);
    cached.invalidate(`suspects:${userId}:10`);
  },
  recordScan: (userId, productId, source) =>
    req("POST", "/scans", { user_id: userId, product_id: productId, source }),

  // Products
  getProduct: (id) => req("GET", `/products/${encodeURIComponent(id)}`),
  getProductByBarcode: (barcode) => req("GET", `/products/by-barcode/${encodeURIComponent(barcode)}`),
  upsertProduct: (payload) => req("POST", "/products", payload),

  // Scans + reactions
  createScan: (payload) => req("POST", "/scans", payload),
  listScans: (userId) => req("GET", `/scans?user_id=${encodeURIComponent(userId)}`),
  createReaction: async (payload) => {
    const res = await req("POST", "/reactions", payload);
    // Reactions can flip suspect scores immediately, so blow away both
    // caches for this user — the next nav reload will refetch.
    try {
      if (payload?.user_id) {
        api.invalidateSuspects(payload.user_id);
        cached.invalidate(`users:${payload.user_id}`);
      }
    } catch { /* */ }
    return res;
  },
  listReactions: (userId) => req("GET", `/reactions?user_id=${encodeURIComponent(userId)}`),

  // Insights / analytics
  listIngredientStats: (userId) => req("GET", `/ingredient-stats?user_id=${encodeURIComponent(userId)}`),
  reactionsCount: (userId) => req("GET", `/reactions/count/${encodeURIComponent(userId)}`),
  getSafety: (productId, userId) =>
    req("GET", `/products/${productId}/safety?user_id=${encodeURIComponent(userId)}`),

  // Insights CRUD
  listInsights: (userId, unreadOnly = false) =>
    req("GET", `/insights/${encodeURIComponent(userId)}${unreadOnly ? "?unread_only=true" : ""}`),
  markInsightSeen: (id) => req("PATCH", `/insights/${encodeURIComponent(id)}`),

  // Catalog recommendations (safe alternatives)
  catalogRecommendations: (ingredient, limit = 3) =>
    req("GET", `/catalog/recommendations?ingredient=${encodeURIComponent(ingredient)}&limit=${limit}`),

  // Daily check-ins
  createDailyCheckin: (payload) => req("POST", "/daily-checkins", payload),
  getTodayCheckin: (userId) => req("GET", `/daily-checkins/${encodeURIComponent(userId)}/today`),

  // Lemon Squeezy subscriptions
  lemonsqueezyCreateCheckout: (plan) =>
    req("POST", "/lemonsqueezy/create-checkout", { plan, origin_url: window.location.origin }),

  // Photo-OCR ingredient analysis (Claude vision)
  analyzeIngredientsPhoto: (payload) => req("POST", "/analyze-ingredients-photo", payload),
  updateProductIngredients: (productId, ingredients) =>
    req("POST", `/products/${encodeURIComponent(productId)}/ingredients`, { ingredients }),
  getPhotoOcrQuota: (userId) => req("GET", `/photo-ocr/quota/${encodeURIComponent(userId)}`),

  // Barcode scans — free tier lifetime cap
  getBarcodeScanQuota: (userId) => req("GET", `/barcode-scan/quota/${encodeURIComponent(userId)}`),
  createBarcodeScan: (userId, productId) =>
    req("POST", "/scans/barcode", { user_id: userId, product_id: productId }),

  // Launch-offer countdown
  getSignupCount: () => req("GET", "/stats/signups"),

  // Skin check-ins (guided face-selfie + Claude Vision analysis)
  createSkinCheckin: (payload) => req("POST", "/skin-checkins", payload),
  listSkinCheckins: (userId, limit = 30) =>
    req("GET", `/skin-checkins?user_id=${encodeURIComponent(userId)}&limit=${limit}`),
  getSkinCheckin: (id) => req("GET", `/skin-checkins/${encodeURIComponent(id)}`),

  // Open Beauty Facts
  openBeautyByBarcode: (barcode) => req("GET", `/openbeauty/${encodeURIComponent(barcode)}`),
  openBeautySearch: (q) => req("GET", `/openbeauty/search?q=${encodeURIComponent(q)}`),
};

export default api;
